import { useEffect, useRef, useState, useCallback } from 'react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import type { DateRange } from '../../components/DateRangePicker/DateRangePicker';
import request from '../../utils/request';
import { MobileResponsiveTable } from '../../components/MobileResponsiveTable';

// ─────────────────────────────────────────────
// 类型定义
// ─────────────────────────────────────────────

// 每日请求数据点
interface DailyRequestItem {
  date: string;
  request_count: number;
}

// 每日 Token 数据点
interface DailyTokenItem {
  date: string;
  token_count: number;
}

// 每日营收数据点
interface DailyRevenueItem {
  date: string;
  amount_usd: number;
}

// 用户消费排名数据项
interface TopUserItem {
  user_name: string;
  organization_name: string;
  cost_usd: number;
}

// tooltip 数据项类型
interface TooltipDataItem {
  axisValue?: string;
  marker?: string;
  value?: number | string | null;
  name?: string;
}

// 仪表盘统计响应
interface DashboardStatsResponse {
  daily_requests: DailyRequestItem[];
  daily_tokens: DailyTokenItem[];
  daily_revenue: DailyRevenueItem[];
  top_users: TopUserItem[];
}

// ─────────────────────────────────────────────
// 常量
// ─────────────────────────────────────────────

// 默认日期范围：最近两周
const DEFAULT_DAYS_RANGE = 14;
// 用户排名默认显示数量
const TOP_USER_LIMIT = 20;

// ─────────────────────────────────────────────
// 工具函数
// ─────────────────────────────────────────────

// 格式化金额为 USD 显示
const formatUsd = (amount: number): string => {
  if (amount >= 1000) {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${amount.toFixed(2)}`;
};

// 格式化请求数
const formatCount = (count: number): string => {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M';
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K';
  }
  return count.toLocaleString();
};

// Token 图表类型
type TokenChartType = 'line' | 'bar';

export default function HomePage() {
  // 获取当前登录用户
  const { user } = useAuthStore();

  // 日期范围状态（默认最近两周）
  const [dateRange, setDateRange] = useState<DateRange>({
    start_date: dayjs().subtract(DEFAULT_DAYS_RANGE, 'day').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
  });

  // 数据状态
  const [dailyRequests, setDailyRequests] = useState<DailyRequestItem[]>([]);
  const [dailyTokens, setDailyTokens] = useState<DailyTokenItem[]>([]);
  const [dailyRevenue, setDailyRevenue] = useState<DailyRevenueItem[]>([]);
  const [topUsers, setTopUsers] = useState<TopUserItem[]>([]);
  const [loading, setLoading] = useState(false);
  // Token 图表类型状态
  const [tokenChartType, setTokenChartType] = useState<TokenChartType>('line');

  // 图表实例引用
  const requestChartRef = useRef<HTMLDivElement>(null);
  const tokenChartRef = useRef<HTMLDivElement>(null);
  const revenueChartRef = useRef<HTMLDivElement>(null);
  const requestChartInstance = useRef<echarts.ECharts | null>(null);
  const tokenChartInstance = useRef<echarts.ECharts | null>(null);
  const revenueChartInstance = useRef<echarts.ECharts | null>(null);

  // ─────────────────────────────────────────
  // 数据请求
  // ─────────────────────────────────────────

  // 获取仪表盘统计数据
  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    const res = await request.get<DashboardStatsResponse>('/gradmin/dashboard/stats', {
      params: {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      },
    });
    // 设置每日请求数据
    setDailyRequests(res.data.daily_requests || []);
    // 设置每日 Token 数据
    setDailyTokens(res.data.daily_tokens || []);
    // 设置每日营收数据
    setDailyRevenue(res.data.daily_revenue || []);
    // 设置用户排名数据（取前 TOP_USER_LIMIT 条）
    setTopUsers((res.data.top_users || []).slice(0, TOP_USER_LIMIT));
    setLoading(false);
  }, [dateRange]);

  // 日期变更时重新请求数据
  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // ─────────────────────────────────────────
  // 请求数折线图
  // ─────────────────────────────────────────

  useEffect(() => {
    if (!requestChartRef.current || dailyRequests.length === 0) return;

    // 容器被重新挂载时销毁旧实例（条件渲染导致 DOM 元素更换）
    if (
      requestChartInstance.current &&
      requestChartInstance.current.getDom() !== requestChartRef.current
    ) {
      requestChartInstance.current.dispose();
      requestChartInstance.current = null;
    }

    // 初始化或获取已有实例
    if (!requestChartInstance.current) {
      requestChartInstance.current = echarts.init(requestChartRef.current);
    }

    // 提取日期和请求数
    const dates = dailyRequests.map(item => item.date);
    const counts = dailyRequests.map(item => item.request_count);

    // 折线图配置
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        // tooltip 格式化
        formatter: (params: unknown) => {
          // 解析 tooltip 参数
          const dataParams = Array.isArray(params) ? params : [params];
          const tooltipItems = dataParams.filter(
            (item): item is TooltipDataItem => typeof item === 'object' && item !== null
          );
          if (tooltipItems.length === 0) return '';
          const firstItem = tooltipItems[0];
          return `${firstItem.axisValue || ''}<br/>${firstItem.marker || ''} Requests: <b>${formatCount(Number(firstItem.value || 0))}</b>`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dates,
        // x 轴标签格式化（只显示月-日）
        axisLabel: {
          formatter: (value: string) => dayjs(value).format('MM-DD'),
          rotate: dates.length > 14 ? 45 : 0,
        },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        // y 轴标签格式化
        axisLabel: {
          formatter: (value: number) => formatCount(value),
        },
      },
      series: [
        {
          name: 'Requests',
          type: 'line',
          data: counts,
          smooth: true,
          // 折线颜色
          itemStyle: { color: '#6467f2' },
          // 面积填充渐变
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(100, 103, 242, 0.25)' },
              { offset: 1, color: 'rgba(100, 103, 242, 0.02)' },
            ]),
          },
        },
      ],
    };

    // 设置图表选项（清除旧数据后重新绘制）
    requestChartInstance.current.setOption(option, true);
    // 确保容器尺寸正确
    requestChartInstance.current.resize();

    return () => {};
  }, [dailyRequests]);

  // ─────────────────────────────────────────
  // Token 数量图表（折线/柱状可切换）
  // ─────────────────────────────────────────

  useEffect(() => {
    if (!tokenChartRef.current || dailyTokens.length === 0) return;

    // 容器被重新挂载时销毁旧实例
    if (
      tokenChartInstance.current &&
      tokenChartInstance.current.getDom() !== tokenChartRef.current
    ) {
      tokenChartInstance.current.dispose();
      tokenChartInstance.current = null;
    }

    // 初始化或获取已有实例
    if (!tokenChartInstance.current) {
      tokenChartInstance.current = echarts.init(tokenChartRef.current);
    }

    // 提取日期和 Token 数
    const dates = dailyTokens.map(item => item.date);
    const counts = dailyTokens.map(item => item.token_count);

    // 根据当前图表类型生成 series 配置
    const seriesConfig: echarts.SeriesOption =
      tokenChartType === 'line'
        ? {
            name: 'Tokens',
            type: 'line',
            data: counts,
            smooth: true,
            // 折线颜色
            itemStyle: { color: '#f59e0b' },
            // 面积填充渐变
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(245, 158, 11, 0.25)' },
                { offset: 1, color: 'rgba(245, 158, 11, 0.02)' },
              ]),
            },
          }
        : {
            name: 'Tokens',
            type: 'bar',
            data: counts,
            // 柱子颜色
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#f59e0b' },
                { offset: 1, color: '#d97706' },
              ]),
              borderRadius: [4, 4, 0, 0],
            },
            barMaxWidth: 32,
          };

    // 图表配置
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        // tooltip 格式化
        formatter: (params: unknown) => {
          const dataParams = Array.isArray(params) ? params : [params];
          const tooltipItems = dataParams.filter(
            (item): item is TooltipDataItem => typeof item === 'object' && item !== null
          );
          if (tooltipItems.length === 0) return '';
          const firstItem = tooltipItems[0];
          return `${firstItem.axisValue || ''}<br/>${firstItem.marker || ''} Tokens: <b>${formatCount(Number(firstItem.value || 0))}</b>`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dates,
        // x 轴标签格式化
        axisLabel: {
          formatter: (value: string) => dayjs(value).format('MM-DD'),
          rotate: dates.length > 14 ? 45 : 0,
        },
        boundaryGap: tokenChartType === 'bar',
      },
      yAxis: {
        type: 'value',
        // y 轴标签格式化
        axisLabel: {
          formatter: (value: number) => formatCount(value),
        },
      },
      series: [seriesConfig],
    };

    // 设置图表选项
    tokenChartInstance.current.setOption(option, true);
    // 确保容器尺寸正确
    tokenChartInstance.current.resize();

    return () => {};
  }, [dailyTokens, tokenChartType]);

  // ─────────────────────────────────────────
  // 营收柱状图
  // ─────────────────────────────────────────

  useEffect(() => {
    if (!revenueChartRef.current || dailyRevenue.length === 0) return;

    // 容器被重新挂载时销毁旧实例（条件渲染导致 DOM 元素更换）
    if (
      revenueChartInstance.current &&
      revenueChartInstance.current.getDom() !== revenueChartRef.current
    ) {
      revenueChartInstance.current.dispose();
      revenueChartInstance.current = null;
    }

    // 初始化或获取已有实例
    if (!revenueChartInstance.current) {
      revenueChartInstance.current = echarts.init(revenueChartRef.current);
    }

    // 提取日期和金额
    const dates = dailyRevenue.map(item => item.date);
    const amounts = dailyRevenue.map(item => item.amount_usd);

    // 柱状图配置
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        // tooltip 格式化
        formatter: (params: unknown) => {
          // 解析 tooltip 参数
          const dataParams = Array.isArray(params) ? params : [params];
          const tooltipItems = dataParams.filter(
            (item): item is TooltipDataItem => typeof item === 'object' && item !== null
          );
          if (tooltipItems.length === 0) return '';
          const firstItem = tooltipItems[0];
          return `${firstItem.axisValue || ''}<br/>${firstItem.marker || ''} Revenue: <b>${formatUsd(Number(firstItem.value || 0))}</b>`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dates,
        // x 轴标签格式化（只显示月-日）
        axisLabel: {
          formatter: (value: string) => dayjs(value).format('MM-DD'),
          rotate: dates.length > 14 ? 45 : 0,
        },
      },
      yAxis: {
        type: 'value',
        // y 轴标签格式化
        axisLabel: {
          formatter: (value: number) => formatUsd(value),
        },
      },
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          data: amounts,
          // 柱子颜色
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#22c55e' },
              { offset: 1, color: '#16a34a' },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          barMaxWidth: 32,
        },
      ],
    };

    // 设置图表选项（清除旧数据后重新绘制）
    revenueChartInstance.current.setOption(option, true);
    // 确保容器尺寸正确
    revenueChartInstance.current.resize();

    return () => {};
  }, [dailyRevenue]);

  // ─────────────────────────────────────────
  // 图表响应式 resize
  // ─────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => {
      // 折线图自适应
      requestChartInstance.current?.resize();
      // Token 图表自适应
      tokenChartInstance.current?.resize();
      // 柱状图自适应
      revenueChartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      // 销毁图表实例
      requestChartInstance.current?.dispose();
      tokenChartInstance.current?.dispose();
      revenueChartInstance.current?.dispose();
      requestChartInstance.current = null;
      tokenChartInstance.current = null;
      revenueChartInstance.current = null;
    };
  }, []);

  return (
    <div className="min-h-full">
      {/* 页面标题和日期选择器 */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#e4e4e7] max-md:flex-col max-md:gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-[#090911] tracking-tight">Home</h1>
          <p className="text-base text-[#71717a]">Welcome back, {user?.name || 'User'}!</p>
        </div>
        <div className="flex items-center gap-3 max-md:w-full">
          {/* 日期范围选择器 */}
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            maxDate={dayjs().format('YYYY-MM-DD')}
          />
        </div>
      </div>

      {/* 图表区域：请求折线图 + 营收柱状图 */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        {/* 请求数折线图 */}
        <div className="bg-white border border-[#e4e4e7] rounded-xl p-5 shadow-sm">
          <div className="text-base font-semibold text-[#090911] mb-4">Requests</div>
          <div className="relative">
            {/* 图表容器始终保留在 DOM，避免 echarts 实例失效 */}
            <div ref={requestChartRef} className="w-full h-80" />
            {/* 加载和空状态覆盖层 */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                Loading...
              </div>
            )}
            {!loading && dailyRequests.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                No data
              </div>
            )}
          </div>
        </div>

        {/* Token 数量图表（折线/柱状可切换） */}
        <div className="bg-white border border-[#e4e4e7] rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-[#090911]">Tokens</div>
            <div className="flex gap-0.5 bg-[#fafafa] rounded-md p-0.5">
              <button
                className={`px-3 py-1 text-sm font-medium border-none rounded-sm cursor-pointer transition-all duration-150 ${tokenChartType === 'line' ? 'bg-white text-[#090911] shadow-sm' : 'bg-transparent text-[#71717a] hover:text-[#090911]'}`}
                onClick={() => setTokenChartType('line')}
              >
                Line
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium border-none rounded-sm cursor-pointer transition-all duration-150 ${tokenChartType === 'bar' ? 'bg-white text-[#090911] shadow-sm' : 'bg-transparent text-[#71717a] hover:text-[#090911]'}`}
                onClick={() => setTokenChartType('bar')}
              >
                Bar
              </button>
            </div>
          </div>
          <div className="relative">
            <div ref={tokenChartRef} className="w-full h-80" />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                Loading...
              </div>
            )}
            {!loading && dailyTokens.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                No data
              </div>
            )}
          </div>
        </div>

        {/* 营收柱状图 */}
        <div className="bg-white border border-[#e4e4e7] rounded-xl p-5 shadow-sm">
          <div className="text-base font-semibold text-[#090911] mb-4">Total Revenue</div>
          <div className="relative">
            {/* 图表容器始终保留在 DOM，避免 echarts 实例失效 */}
            <div ref={revenueChartRef} className="w-full h-80" />
            {/* 加载和空状态覆盖层 */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                Loading...
              </div>
            )}
            {!loading && dailyRevenue.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white text-[#71717a] text-sm z-[2]">
                No data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 用户消费排名表格 */}
      <div className="bg-white border border-[#e4e4e7] rounded-xl p-5 shadow-sm">
        <div className="text-base font-semibold text-[#090911] mb-4">Top Users by Spending</div>
        {loading ? (
          <div className="flex items-center justify-center h-80 text-[#71717a] text-sm">
            Loading...
          </div>
        ) : topUsers.length === 0 ? (
          <div className="flex items-center justify-center h-80 text-[#71717a] text-sm">No data</div>
        ) : (
          <MobileResponsiveTable
            containerClassName="max-h-[520px] overflow-y-auto overflow-x-auto border border-[#e4e4e7] rounded-lg"
            tableClassName="w-full border-collapse text-sm"
          >
            <thead className="sticky top-0 z-[1] bg-[#fafafa]">
              <tr>
                <th className="w-12 text-center px-4 py-2.5 font-semibold text-[#71717a] border-b border-[#e4e4e7] whitespace-nowrap">
                  #
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-[#71717a] border-b border-[#e4e4e7] whitespace-nowrap">
                  User
                </th>
                <th className="px-4 py-2.5 text-left font-semibold text-[#71717a] border-b border-[#e4e4e7] whitespace-nowrap">
                  Organization
                </th>
                <th className="px-4 py-2.5 text-right font-semibold text-[#71717a] border-b border-[#e4e4e7] whitespace-nowrap tabular-nums">
                  Amount (USD)
                </th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((item, index) => (
                <tr
                  key={`${item.user_name}-${item.organization_name}-${index}`}
                  className="hover:bg-black/[0.04] last:[&>td]:border-b-0"
                >
                  <td className="w-12 text-center px-4 py-2.5 font-semibold text-[#71717a] border-b border-[#e4e4e7]">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2.5 text-[#090911] border-b border-[#e4e4e7]">
                    {item.user_name}
                  </td>
                  <td className="px-4 py-2.5 text-[#090911] border-b border-[#e4e4e7]">
                    {item.organization_name}
                  </td>
                  <td className="px-4 py-2.5 text-[#090911] border-b border-[#e4e4e7] text-right tabular-nums">
                    {formatUsd(item.cost_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </MobileResponsiveTable>
        )}
      </div>
    </div>
  );
}
